import { createRouter, createWebHistory } from "vue-router";
import HomeView from "../views/HomeView.vue";
import LoginView from "@/views/LoginView.vue";
import Contrat from "@/views/ContactView.vue";
import StrykerCommands from "@/views/StrykerCommands.vue";
import StrykerOneCommands from "@/views/StrykerOneCommands.vue";
import ContratView from "@/views/ContratView.vue";
import AdminView from "@/views/AdminView.vue";
import OneStrykerService from "@/views/OneStrykerService.vue";
import CartView from "@/views/CartView.vue";
import PointView from "@/views/PointView.vue";
import SelectCustomerView from "@/views/SelectCustomerView.vue";
import PrivacyView from "@/views/PrivacyView.vue";
import AccessibilityView from "@/views/AccessibilityView.vue";
import DisclaimerView from "@/views/DisclaimerView.vue";
import TermsOfUseView from "@/views/TermsOfUseView.vue";
import CreateOrResetPasswordView from "@/views/CreateOrResetPasswordView.vue";
import SiteMapView from "@/views/SiteMapView.vue";
import KPIView from "@/views/KPIView.vue";
import ProjectionView from "@/views/ProjectionView.vue";

import store from "../store";
import axios from "axios";

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
  },
  {
    path: "/login",
    name: "login",
    component: LoginView,
  },
  {
    path: "/contact",
    name: "contact",
    component: Contrat,
  },
  {
    path: "/stryker-commands",
    name: "stryker-commands",
    component: StrykerCommands,
  },
  {
    path: "/stryker-one-commands",
    name: "stryker-one-commands",
    component: StrykerOneCommands,
  },
  {
    path: "/contrat",
    name: "contrat",
    component: ContratView,
  },
  {
    path: "/admin",
    name: "admin",
    component: AdminView,
  },
  {
    path: "/service-one-stryker",
    name: "service-one-stryker",
    component: OneStrykerService,
  },
  {
    path: "/points",
    name: "points",
    component: PointView,
  },
  {
    path: "/cart",
    name: "cart",
    component: CartView,
  },
  {
    path: "/select-customer",
    name: "select-customer",
    component: SelectCustomerView,
  },
  {
    path: "/privacy",
    name: "privacy",
    component: PrivacyView,
  },
  {
    path: "/accessibility",
    name: "accessibility",
    component: AccessibilityView,
  },
  {
    path: "/disclaimer",
    name: "disclaimer",
    component: DisclaimerView,
  },
  {
    path: "/terms-of-use",
    name: "terms-of-use",
    component: TermsOfUseView,
  },
  {
    path: "/sitemap",
    name: "sitemap",
    component: SiteMapView,
  },
  {
    path: "/kpi",
    name: "kpi",
    component: KPIView,
  },
  {
    path: "/projections",
    name: "projections",
    component: ProjectionView,
  },
  {
    path: "/resetpassword",
    name: "resetpassword",
    component: CreateOrResetPasswordView,
    props: (route) => ({
      userId: route.query.userId,
      token: decodeURIComponent(route.query.token).replace(/ /g, "+"),
    }),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      store.commit("auth/clearAuthData");
      if (router.currentRoute.value.path !== "/login") {
        router.push("/login");
      }
    }
    return Promise.reject(error);
  },
);

router.beforeEach(async (to, from, next) => {
  // Constants
  const publicPages = [
    "/login",
    "/privacy",
    "/accessibility",
    "/disclaimer",
    "/terms-of-use",
    "/resetpassword",
    "/sitemap",
  ];
  const customerIdPages = [
    "/",
    "/stryker-one-commands",
    "/stryker-commands",
    "/contrat",
    "/service-one-stryker",
    "/points",
  ];

  // 1. Authentication Check
  const token = store.state.auth.token || localStorage.getItem("authToken");
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    store.commit("auth/setToken", token);
  }

  const loggedIn = !!token;
  // 2. User Info Check
  try {
    if (loggedIn && !store.state.users.user) {
      await store.dispatch("users/getUserInfosWithCustomers");
    }

    const user = store.state.users.user;
    const isAdmin = user?.userRole === "ADMIN";
    const isReps = user?.userRole === "REPS";
    const isBud = user?.userRole === "BUD";
    const isRsm = user?.userRole === "RSM";
    const isCustomer = user?.userRole === "CUSTOMER";
    const userEmail = user?.email || store.state.auth.user?.username;
    const isStrykerEmployee = !!userEmail?.endsWith("@stryker.com");

    // 3. Customer Check
    let selectedCustomer =
      store.state.users.selectedCustomer.id != -1
        ? store.state.users.selectedCustomer
        : JSON.parse(localStorage.getItem("cust_stryker_one"));

    if (selectedCustomer) {
      store.commit("users/setSelectedCustomer", selectedCustomer);

      // Load necessary data for specific routes
      if (to.path === "/stryker-commands" && !from.name) {
        store.commit("setOverlay", true);
        await Promise.all([
          store.dispatch(
            "strykerCommands/fetchCustommerCommands",
            selectedCustomer.id,
          ),
          store.dispatch("statsCustomer/fetChSpecificStatsCustomer", {
            id: selectedCustomer.id,
            name: isAdmin ? "admin" : "",
          }),
        ])
          .then(() => {
            store.commit("setOverlay", false);
          })
          .catch(() => {
            store.commit("setOverlay", false);
          });
      }

      if (to.path === "/" && from.path !== "/admin") {
        store.commit("setOverlay", true);
        await Promise.all([
          store.dispatch("users/getUserInfosWithCustomers"),
          store.dispatch("statsCustomer/fetChSpecificStatsCustomer", {
            id: selectedCustomer.id,
            name: isAdmin ? "admin" : "",
          }),
          store.dispatch("cart/fetchCartByCustomer", selectedCustomer.id),
        ])
          .then(() => {
            store.commit("setOverlay", false);
          })
          .catch(() => {
            store.commit("setOverlay", false);
          });
      }

      if (to.path === "/stryker-one-commands" && !from.name) {
        store.commit("setOverlay", true);
        const dataToFetch = {
          pagination: "",
          customer: selectedCustomer.id,
        };
        await Promise.all([
          store.dispatch(
            "ordersOneStryker/fetchCustommerCommands",
            dataToFetch,
          ),
          store.dispatch("statsCustomer/fetChSpecificStatsCustomer", {
            id: selectedCustomer.id,
            name: isAdmin ? "admin" : "",
          }),
        ])
          .then(() => {
            store.commit("setOverlay", false);
          })
          .catch(() => {
            store.commit("setOverlay", false);
          });
      }
    }

    // 4. Navigation Guards
    // PRIORITÉ 1: Redirection forcée vers projections pour REPS/BUD/RSM sans client
    // Ces utilisateurs ne peuvent accéder qu'à /projections et /login
    const userCustomers = store.state.users.customers || [];
    if (
      loggedIn &&
      !isAdmin &&
      !selectedCustomer &&
      (isReps || isBud || isRsm || isStrykerEmployee) &&
      userCustomers.length === 0
    ) {
      if (to.path !== "/projections" && to.path !== "/login") {
        return next("/projections");
      }
    }

    if (loggedIn && to.path === "/login") {
      // Redirection intelligente après login
      if (
        !selectedCustomer &&
        (isReps || isBud || isRsm || isStrykerEmployee) &&
        userCustomers.length === 0
      ) {
        return next("/projections");
      }
      if (!selectedCustomer && !isAdmin) {
        return next("/select-customer");
      }
      if (isAdmin && !selectedCustomer) {
        return next("/admin");
      }
      return next("/");
    }

    if (loggedIn && to.path === "/points" && !from.name) {
      return next("/");
    }

    if (loggedIn && to.path === "/cart" && !from.name) {
      return next("/");
    }

    if (!loggedIn && !publicPages.includes(to.path)) {
      return next("/login");
    }

    if (loggedIn && customerIdPages.includes(to.path) && !selectedCustomer) {
      if (!isAdmin) {
        // Si l'utilisateur est REPS, BUD ou RSM sans aucun client (0) -> redirection vers projections
        if (
          (isReps || isBud || isRsm || isStrykerEmployee) &&
          userCustomers.length === 0
        ) {
          return next("/projections");
        }
        // Si c'est un CUSTOMER sans customer lié -> déconnexion (ne devrait pas arriver)
        if (isCustomer) {
          store.commit("auth/clearAuthData");
          return next("/login");
        }
        // Autres cas -> sélection du customer
        return next("/select-customer");
      }
      return next("/admin");
    }

    // Protection de la route admin
    if (loggedIn && to.path === "/admin" && !isAdmin) {
      return next("/");
    }

    // Protection de la route projections (REPS, BUD, RSM, ADMIN et employés @stryker.com uniquement - pas CUSTOMER)
    if (
      loggedIn &&
      to.path === "/projections" &&
      !isAdmin &&
      !isReps &&
      !isBud &&
      !isRsm &&
      !isStrykerEmployee
    ) {
      return next("/");
    }

    // 5. Route Validation
    if (!router.hasRoute(to.name) && to.path !== "/") {
      return next("/");
    }

    return next();
  } catch {
    if (!publicPages.includes(to.path)) {
      store.commit("auth/clearAuthData");
      return next("/login");
    }
    return next();
  }
});

export default router;
